import {Utils} from "../shared/Utils";
import {isArray} from "chart.js/helpers";

const initializedSelectInstances = new Map<string, MaterialSelect<any>>();
const initializedElementInstances = new WeakMap<HTMLElement, MaterialSelect<any>>();

interface DataItem<T extends OptionData> {
    text: string;
    value: string;
    data: T;
}

export interface OptionData {
    value: string;
    text: string;
}

export interface MaterialSelectOptions<T extends OptionData> {
    items?: (DataItem<T> | T)[] | (() => Promise<T[]>);
    onSelect?: (selectedItems: DataItem<T>[]) => void;
    multiSelect?: boolean;
    placeholder?: string;
    searchable?: boolean;
    enableSelectAll?: boolean;
    noResultsText?: string;
    searchPlaceholder?: string;
    itemRenderer?: (item: DataItem<T>) => string;
    initialSelection?: string | string[] | DataItem<T>[];
    storageKey?: string;
}

export class MaterialSelect<T extends OptionData = OptionData> {
    public readonly elementId: string;
    private readonly anchorElement: HTMLElement;
    private boundHandlers = new Map();
    private dropdownBoundHandlers = new Map();
    private _container!: HTMLElement;
    private options: Required<MaterialSelectOptions<T>>;
    private placeholderText = 'Select...';
    private focusedOptionIndex = -1;
    private selectedItems: DataItem<T>[];
    private liveRegion: HTMLElement;
    private selectTrigger: HTMLInputElement;
    private _dropdown: HTMLElement | null = null;
    private searchInputContainer: HTMLElement | null = null;
    private searchInput: HTMLInputElement | null = null;
    private optionsList: HTMLElement | null = null;
    private inputField: HTMLElement | null;
    public isDestroyed = false;
    private allOptionElements: HTMLElement[] = [];
    private noResultsElement: HTMLElement | null = null;
    private _observer: IntersectionObserver | null = null;
    private _resolvedItems: DataItem<T>[] = [];
    private _nativeSelect: HTMLSelectElement | null = null;

    get items(): DataItem<T>[] {
        // Return cached resolved items if we have them, otherwise try to get from options
        if (this._resolvedItems.length > 0) return this._resolvedItems;
        if (this.options.items instanceof Function) return [];//Async function return nothing until resolved.
        return this.options.items.map(item => this.createDataItemFromValue(item));
    }

    get selected(): string | null {
        if (this.selectedItems.length === 0) return null;
        return this.selectedItems[0].value;
    }

    get selectedItemsData(): DataItem<T>[] {
        return this.selectedItems;
    }

    get selectedText(): string | null {
        if (this.selectedItems.length === 0) return null;
        return this.selectedItems[0].text;
    }

    get selectedIds(): string[] {
        if (this.selectedItems.length === 0) return [];
        return this.selectedItems.map(item => item.value);
    }

    /**
     * Accepts numbers as well as strings — see {@link MaterialSelectElement.selected} for why that
     * matters. `ids.includes(item.value)` was a strict comparison against values that are ALWAYS
     * strings, so a caller passing numeric ids selected nothing and was told nothing.
     */
    set selectedIds(ids: (string | number)[]) {
        const wanted = (ids ?? []).map(id => String(id));
        this.selectedItems = this.items.filter(item => wanted.includes(String(item.value)));

        const missing = wanted.filter(id => !this.selectedItems.some(item => String(item.value) === id));
        if (missing.length) this.warnUnmatched(missing.join(', '), 'selectedIds');

        this.updateTriggerText();
        this.options.onSelect(this.selectedItems);
        this.saveSelection();
        if (this.isOpen()) {
            this.filterOptions();
        }
    }

    get container(){
        return this._container;
    }

    get dropdown(){
        return this._dropdown;
    }

    /**
     * Selects the option whose value matches `id`.
     *
     * <p>`id` accepts a number on purpose. Item values are stringified on the way in
     * ({@link createDataItemFromValue} does `String(value)`), so the old strict `item.value === id`
     * could NEVER match a numeric id — and on a miss this setter did nothing at all and returned
     * quietly, leaving whatever the select was already showing. A form then saved that stale value
     * as if the user had chosen it.</p>
     *
     * <p>This was not theoretical: passing `room.roomType.id` (a number) re-typed a room from
     * Semi-Sharing to Single on an unrelated save, on real data. The callers could not be blamed by
     * the compiler either — most of them read from `any`-typed payloads.</p>
     *
     * <p>A miss now warns instead of passing silently. Expect that warning to also surface a second,
     * separate timing bug: setting a selection BEFORE an async `items` promise resolves discards it.
     * That one is deliberately left visible rather than guessed at.</p>
     */
    set selected(id: string | number | DataItem<T> | null) {
        if(this.isDataItem(id)){
            this.selectedItems = [id];
            this.updateTriggerText();
            this.options.onSelect(this.selectedItems);
            this.saveSelection();
            if (this.isOpen()) {
                this.filterOptions();
            }
            return;
        }
        if (!id) {
            this.selectedItems = [];
            this.updateTriggerText();
            this.options.onSelect(this.selectedItems);
            this.saveSelection();
            if (this.isOpen()) {
                this.filterOptions();
            }
            return;
        }
        const wanted = String(id);
        const item = this.items.find(item => String(item.value) === wanted);
        if(item){
            this.selectedItems = [item];
            this.updateTriggerText();
            this.options.onSelect(this.selectedItems);
            this.saveSelection();
            if (this.isOpen()) {
                this.filterOptions();
            }
        } else {
            this.warnUnmatched(wanted, 'selected');
        }
    }

    /**
     * A selection that matched nothing leaves the component on its previous value, which is
     * indistinguishable from a successful set — that silence is what made the room re-typing bug
     * invisible. Say so, and show what WAS available, since an empty item list means the caller set
     * a value before the options loaded.
     */
    private warnUnmatched(id: string, setter: string): void {
        console.warn(
            `MaterialSelect.${setter}: no option matches "${id}" — the selection was ignored and the ` +
            `component kept its previous value. Available: ` +
            (this.items.length ? this.items.map(item => item.value).join(', ') : '(none — items not loaded yet)')
        );
    }

    set disabled(state: boolean) {
        if (state) {
            this.selectTrigger.setAttribute('disabled', 'true');
            this.selectTrigger.classList.add('disabled');
        } else {
            this.selectTrigger.removeAttribute('disabled');
            this.selectTrigger.classList.remove('disabled');
        }
    }

    private createTrigger(): HTMLInputElement {
        const selectTrigger = document.createElement('input');
        selectTrigger.type = 'text';
        selectTrigger.readOnly = true;
        selectTrigger.classList.add('material-select-trigger');
        selectTrigger.setAttribute('role', 'button');
        selectTrigger.setAttribute('aria-haspopup', 'listbox');
        selectTrigger.setAttribute('aria-expanded', 'false');
        selectTrigger.setAttribute('tabindex', '0'); // Make the trigger focusable
        return selectTrigger;
    }

    private constructor(element: HTMLElement | string, options: MaterialSelectOptions<T> = {}) {
        const el = typeof element === 'string' ? document.querySelector<HTMLElement>(element) : element;
        if (!el) {
            throw new Error(`MaterialSelect: Element not found for selector "${element}"`);
        }
        this.anchorElement = el;

        let nativeSelect: HTMLSelectElement | null = null;

        // Step 1: Establish the container and find the native select if it exists.
        if (el.tagName.toLowerCase() === 'select') {
            nativeSelect = el as HTMLSelectElement;
            // If the select is already wrapped, use its parent as the container.
            if (nativeSelect.parentElement?.classList.contains('material-select-container')) {
                this._container = nativeSelect.parentElement;
            } else {
                // Otherwise, create the wrapper and set it as the container.
                this.replaceNativeSelect(nativeSelect);
            }
        } else {
            // We were initialized on a div, so it's our container.
            this._container = el;
            nativeSelect = el.querySelector('select'); // It might contain a select.
        }
        this._container.classList.add('material-select-container');

        // Store reference to native select for syncing values
        this._nativeSelect = nativeSelect;

        // Step 2: Find or create the trigger input element.
        this.inputField = this._container.closest('.input-field');
        const searchRoot = this.inputField || this._container;
        const existingTrigger = searchRoot.querySelector<HTMLInputElement>('.material-select-trigger');

        if (existingTrigger) {
            // An existing trigger was found, reuse it.
            this.selectTrigger = existingTrigger;
        } else {
            // No trigger found, so create and append one.
            this.selectTrigger = this.createTrigger();
            if (this.inputField) {
                this.inputField.appendChild(this.selectTrigger);
            } else {
                this._container.appendChild(this.selectTrigger);
            }
        }

        //Check if items is a promise
        let optItems: any[] = [];
        if (options.items instanceof Function) {
            options.items().then(resolvedItems => {
                options.items = resolvedItems;
                this._resolvedItems = resolvedItems.map(item => this.createDataItemFromValue(item));
                this.updateOptions(options);
            }).catch(err => {
                console.error("MaterialSelect: Error resolving items promise", err);
                options.items = [];
                this._resolvedItems = [];
                this.updateOptions(options);
            });
        }else{
            optItems = options.items || [];
        }

        // Step 3: Continue with standard initialization logic.
        const allItemsAsDataItems = optItems.map(item => this.createDataItemFromValue(item));
        this._resolvedItems = allItemsAsDataItems; // Cache the resolved items
        this.options = {
            items: allItemsAsDataItems,
            onSelect: options.onSelect || (() => {}),
            placeholder: options.placeholder || this.placeholderText,
            multiSelect: options.multiSelect ?? false,
            searchable: options.searchable ?? false,
            enableSelectAll: options.enableSelectAll ?? false,
            noResultsText: options.noResultsText || "No results found",
            searchPlaceholder: options.searchPlaceholder || "Search...",
            itemRenderer: options.itemRenderer || ((item) => item.text),
            initialSelection: options.initialSelection || [],
            storageKey: options.storageKey || ''
        };

        let items: DataItem<T>[] = [];
        //Check if options.items is a single T element or an array
        if(Array.isArray(this.options.items)){
            items = this.options.items as DataItem<T>[];
        }else{
            //TODO: I dont like this, we need to be sure its what it says it is.
            items = [this.createDataItemFromValue(this.options.items as unknown as T)];
        }

        const initialSelection = this.loadSelection() ?? this.options.initialSelection;
        this.selectedItems = this.getSelectedItemsFromInitial(initialSelection, items);
        this.liveRegion = this.createLiveRegion();

        if (!this._container.id) this._container.id = `material-select-${Math.random().toString(36).substr(2, 9)}`;
        // Prioritize the original element's ID for instance tracking.
        this.elementId = (nativeSelect || el).id || this._container.id;

        this.initializeEvents();

        if (this.selectedItems.length > 0) {
            if (this.inputField) {
                this.inputField.classList.add('active');
            }
        }
        this.updateTriggerText();
        this.addEventHandler(window, 'beforeunload', () => this.destroy());
        this.initializeVisibilityObserver();
        // Set placeholder
        this.selectTrigger.placeholder = this.options.placeholder;
    }

    private loadSelection(): string[] | null {
        if (!this.options.storageKey) {
            return null;
        }
        try {
            const storedValue = localStorage.getItem(this.options.storageKey);
            if (storedValue) {
                const ids = JSON.parse(storedValue);
                if (Array.isArray(ids)) {
                    return ids;
                }
            }
        } catch (e) {
            console.error(`MaterialSelect: Error parsing stored selection for key "${this.options.storageKey}"`, e);
        }
        return null;
    }

    private saveSelection() {
        if (!this.options.storageKey) {
            return;
        }
        try {
            localStorage.setItem(this.options.storageKey, JSON.stringify(this.selectedIds));
        } catch (e) {
            console.error(`MaterialSelect: Error saving selection for key "${this.options.storageKey}"`, e);
        }
    }

    public clearSelection(){
        this.selected = null;
    }

    public updateOptions(options: MaterialSelectOptions<T> = {}) {
        const hasNewItems = options.hasOwnProperty('items');
        const hasNewInitialSelection = options.hasOwnProperty('initialSelection');

        // We can't just Object.assign, as the passed options object is partial.
        // We need to merge the new properties into our existing options.
        for (const key in options) {
            if (Object.prototype.hasOwnProperty.call(options, key)) {
                // A type assertion is needed here because keys are generic.
                (this.options as any)[key] = (options as any)[key];
            }
        }

        // The 'items' array needs special handling to be converted to DataItem objects.
        if (hasNewItems) {
            if(this.options.items instanceof Function){
                this.options.items().then(resolvedItems => {
                    this.options.items = resolvedItems.map(item => this.createDataItemFromValue(item));
                    this._resolvedItems = this.options.items as DataItem<T>[];
                    this.reload();
                }).catch(err => {
                    console.error("MaterialSelect: Error resolving items promise", err);
                    this.options.items = [];
                    this._resolvedItems = [];
                    this.reload();
                });
            }else if (Array.isArray(options.items)) {
                this.options.items = (options.items as (DataItem<T> | T)[]).map((item: DataItem<T> | T) => this.createDataItemFromValue(item));
                this._resolvedItems = this.options.items as DataItem<T>[];
            }
        }

        // If a new initialSelection or new items are provided, we must re-evaluate the selected items.
        if (hasNewInitialSelection || hasNewItems) {
            let items: DataItem<T>[] = [];
            //Check if options.items is a single T element or an array
            if(Array.isArray(this.options.items)) {
                items = this.options.items as DataItem<T>[];
            }else if (Array.isArray(options.items)) {
                this.options.items = (options.items as (DataItem<T> | T)[]).map((item: DataItem<T> | T) => this.createDataItemFromValue(item));
            }

            this.selectedItems = this.getSelectedItemsFromInitial(this.options.initialSelection, items);
            this.updateTriggerText();
        }
    }


    /**
     * Initializes an IntersectionObserver to automatically reload the component
     * when it becomes visible. This is crucial for components initialized in a
     * hidden state (e.g., in a tab or modal).
     * @private
     */
    private initializeVisibilityObserver() {
        if (!this._container) return;

        this._observer = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    console.debug(`MaterialSelect instance ${this.elementId} is now visible. Reloading.`);
                    this.reload();
                    observer.disconnect();
                    this._observer = null;
                }
            });
        }, { threshold: 0.01 });

        this._observer.observe(this._container);
    }

    private getSelectedItemsFromInitial(
        initialSelection: string | string[] | DataItem<T>[] | undefined,
        allItems: DataItem<T>[]
    ): DataItem<T>[] {
        if (!initialSelection || (Array.isArray(initialSelection) && initialSelection.length === 0)) {
            return [];
        }

        const selectionValues = (Array.isArray(initialSelection) ? initialSelection : [initialSelection])
            .map(sel => (this.isDataItem(sel) ? sel.value : String(sel)));

        return allItems.filter(item => selectionValues.includes(item.value));
    }

    private replaceNativeSelect(nativeSelect: HTMLElement) {
        (nativeSelect as HTMLElement).style.display = 'none';
        const newContainer = document.createElement('div');
        newContainer.className = 'material-select-container';
        if(nativeSelect.parentNode) nativeSelect.parentNode.insertBefore(newContainer, nativeSelect);
        else document.body.appendChild(newContainer);
        newContainer.appendChild(nativeSelect);
        this._container = newContainer;
    }

    private createDataItemFromValue(value: any): DataItem<T> {
        if (this.isDataItem(value)) {
            return value;
        }
        const text = String(value);
        const idField = String(value);
        return { text, value: idField, data: value };
    }

    private isDataItem(value: any): value is DataItem<T> {
        return value && typeof value === 'object' && 'value' in value && 'text' in value;
    }

    private createLiveRegion(): HTMLElement {
        const region = document.createElement('div');
        region.setAttribute('aria-live', 'polite');
        region.setAttribute('aria-atomic', 'true');
        region.className = 'm-autocomplete-live-region';
        document.body.appendChild(region);
        return region;
    }

    private addEventHandler(target: EventTarget, event: string, handler: EventListener, handlerMap: Map<any, any> = this.boundHandlers) {
        const boundHandler = handler.bind(this);
        target.addEventListener(event, boundHandler);
        handlerMap.set(boundHandler, [target, event]);
    }

    private initializeEvents() {
        this.addEventHandler(this.selectTrigger, 'click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation(); // Stop other handlers from interfering
            this.toggleDropdown(e);
        });

        this.addEventHandler(this.selectTrigger, 'keydown', (e) => this.handleKeydown(e as KeyboardEvent));

        // Track the global document click handler so it can be removed on cleanup
        this.addEventHandler(document, 'click', (e) => this.handleGlobalDocumentClick(e));
    }

    public reload() {
        if (this.isDestroyed) {
            console.warn(`Attempted to reload a destroyed MaterialSelect instance (${this.elementId}).`);
            return;
        }
        this.rediscoverElements();
        this.removeEvents();
        this.initializeEvents();
        this.recalculateDimensions();
        this.refresh();
    }

    private removeEvents() {
        this.boundHandlers.forEach(([target, eventName], handler) => {
            target.removeEventListener(eventName, handler);
        });
        this.boundHandlers.clear();
    }

    private handleGlobalDocumentClick(event: Event) {
        initializedSelectInstances.forEach((instance) => {
            // If the instance is open and the click occurred outside its container, close it.
            // This works because the dropdown is now a child of the container.
            if (instance.isOpen() && instance._container) {
                const target = event.target as HTMLElement;
                if (!instance._container.contains(target)) {
                    instance.closeDropdown();
                }
            }
        });
    }

    private handleKeydown(e: KeyboardEvent) {
        if (this.options.searchable && document.activeElement === this.searchInput) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.toggleDropdown(e);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (!this.isOpen()) {
                this.openDropdown();
            }
            if (this.options.searchable) {
                this.focusFirstOption();
            }
        } else if (e.key === 'Escape') {
            this.closeDropdown();
        }
    }

    private handleDropdownKeydown(e: KeyboardEvent) {
        if (!this.isOpen() || !this.optionsList) return;

        if (this.options.searchable && document.activeElement === this.searchInput) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                this.focusFirstOption();
            } else if (e.key === 'Escape') {
                this.closeDropdown();
                this.selectTrigger.focus();
            } else if (e.key === 'Tab') {
                this.closeDropdown();
                return;
            }
            return;
        }

        const options = Array.from(this.optionsList.querySelectorAll('.material-select-option:not([style*="display: none"])')) as HTMLElement[];
        if (options.length === 0) return;

        const focusedItem = options.find(option => option.classList.contains('focused'));
        let nextIndex = this.focusedOptionIndex;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            nextIndex = (this.focusedOptionIndex + 1) % options.length;
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            nextIndex = (this.focusedOptionIndex - 1 + options.length) % options.length;
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (focusedItem) {
                (focusedItem as HTMLElement).click();
            }
        } else if (e.key === 'Escape') {
            this.closeDropdown();
            this.selectTrigger.focus();
        } else if (e.key === 'Tab') {
            this.closeDropdown();
            return;
        }

        if (focusedItem) {
            focusedItem.classList.remove('focused');
            focusedItem.setAttribute('tabindex', '-1');
        }
        if (options[nextIndex]) {
            options[nextIndex].classList.add('focused');
            options[nextIndex].setAttribute('tabindex', '0');
            options[nextIndex].focus();
            this.focusedOptionIndex = nextIndex;
        }
    }

    private focusFirstOption() {
        if (!this.optionsList) return;
        const options = Array.from(this.optionsList.querySelectorAll('.material-select-option:not([style*="display: none"])')) as HTMLElement[];
        if (options.length > 0) {
            this.focusedOptionIndex = 0;
            this.focusOption(options[this.focusedOptionIndex]);
        }
    }

    private focusOption(element: HTMLElement) {
        if (!this.optionsList) return;
        const focused = this.optionsList.querySelector('.material-select-option.focused');
        if (focused) {
            focused.classList.remove('focused');
            focused.setAttribute('tabindex', '-1');
        }
        element.classList.add('focused');
        element.setAttribute('tabindex', '0');
        element.focus();
    }

    private isOpen(): boolean {
        return !!this._dropdown;
    }

    toggleDropdown(event?: Event) {
        if (event) {
            event.stopPropagation();
        }
        if (this.isOpen()) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    private closeAllDropdowns() {
        initializedSelectInstances.forEach(instance => {
            if (instance.isOpen()) {
                instance.closeDropdown();
            }
        });
    }

    public openDropdown() {
        if (this.isDestroyed || this.isOpen()) return;
        this.closeAllDropdowns();

        // 1. Create dropdown and its children
        this._dropdown = document.createElement('div');
        this._dropdown.classList.add('material-select-dropdown');
        this._dropdown.setAttribute('role', 'listbox');

        if (this.options.searchable) {
            this.searchInputContainer = this.createSearchInputContainer();
            this._dropdown.appendChild(this.searchInputContainer);
        }
        if (this.options.enableSelectAll && this.options.multiSelect) {
            this.createSelectAllButtons();
        }

        this.optionsList = document.createElement('div');
        this.optionsList.classList.add('material-select-options-list');
        this._dropdown.appendChild(this.optionsList);

        this.noResultsElement = this.createNoResultsElement();
        this.optionsList.appendChild(this.noResultsElement);

        // 2. Append to the main container
        this._container.appendChild(this._dropdown);

        // 3. Populate options
        this.initializeOptions();

        // 4. Add event listeners specific to the dropdown
        this.addEventHandler(this._dropdown, 'keydown', (e) => this.handleDropdownKeydown(e as KeyboardEvent), this.dropdownBoundHandlers);
        if (this.searchInput) {
            this.addEventHandler(this.searchInput, 'input', Utils.debounce(() => this.filterOptions(), 200), this.dropdownBoundHandlers);
        }

        // 5. Show and position
        this._dropdown.classList.add('show');
        document.body.classList.add('material-select-open');
        this.selectTrigger.setAttribute('aria-expanded', 'true');
        this.updateDropdownPosition();

        if (this.inputField) {
            this.inputField.classList.add('active');
        }
        if (this.options.searchable && this.searchInput) {
            this.searchInput.focus();
        }
        this.filterOptions();
    }

    closeDropdown() {
        if (this.isDestroyed || !this.isOpen()) return;

        // 1. Remove event listeners bound to the dropdown
        this.dropdownBoundHandlers.forEach(([target, eventName], handler) => {
            target.removeEventListener(eventName, handler);
        });
        this.dropdownBoundHandlers.clear();

        // 2. Remove dropdown from DOM
        this._dropdown?.remove();

        // 3. Nullify all dropdown-related properties
        this._dropdown = null;
        this.optionsList = null;
        this.searchInput = null;
        this.noResultsElement = null;
        this.searchInputContainer = null;

        // 4. Reset state
        document.body.classList.remove('material-select-open');
        this.selectTrigger.setAttribute('aria-expanded', 'false');

        if (this.inputField && this.selectedItems.length === 0) {
            this.inputField.classList.remove('active');
        }
        this.focusedOptionIndex = -1;
        this.allOptionElements = []; // Clear cached option elements
    }

    public recalculateDimensions() {
        if (this.isDestroyed) return;
        this.updateDropdownPosition();
    }

    private updateDropdownPosition() {
        if (!this._dropdown) return;
        const triggerRect = this.selectTrigger.getBoundingClientRect();
        this._dropdown.style.minWidth = `${triggerRect.width}px`;
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const dropdownHeight = this._dropdown.offsetHeight;
        if (spaceBelow < dropdownHeight && triggerRect.top > dropdownHeight) {
            this._dropdown.classList.add('flip-up');
        } else {
            this._dropdown.classList.remove('flip-up');
        }
    }

    private createSearchInputContainer(): HTMLElement {
        const searchInputContainer = document.createElement('div');
        searchInputContainer.classList.add('material-select-search-container');
        this.searchInput = document.createElement('input');
        this.searchInput.type = 'text';
        this.searchInput.placeholder = this.options.searchPlaceholder;
        this.searchInput.classList.add('material-select-search-input');
        searchInputContainer.appendChild(this.searchInput);
        this.searchInput.setAttribute('tabindex', '-1');
        return searchInputContainer;
    }

    private createSelectAllButtons() {
        if (!this._dropdown) return;
        const buttonsContainer = document.createElement('div');
        buttonsContainer.classList.add('material-select-buttons-container');

        const selectAllBtn = document.createElement('button');
        selectAllBtn.type = 'button';
        selectAllBtn.classList.add('material-select-button', 'material-select-select-all');
        selectAllBtn.textContent = 'Select All';
        this.addEventHandler(selectAllBtn, 'click', () => {
            this.selectedItems = this.items;
            this.updateTriggerText();
            this.options.onSelect(this.selectedItems);
            this.saveSelection();
            this.allOptionElements.forEach(optionEl => {
                optionEl.classList.add('selected');
                optionEl.setAttribute('aria-selected', 'true');
            });
        }, this.dropdownBoundHandlers);

        const selectNoneBtn = document.createElement('button');
        selectNoneBtn.type = 'button';
        selectNoneBtn.classList.add('material-select-button', 'material-select-select-none');
        selectNoneBtn.textContent = 'Select None';
        this.addEventHandler(selectNoneBtn, 'click', () => {
            this.selectedItems = [];
            this.updateTriggerText();
            this.options.onSelect(this.selectedItems);
            this.saveSelection();
            this.allOptionElements.forEach(optionEl => {
                optionEl.classList.remove('selected');
                optionEl.setAttribute('aria-selected', 'false');
            });
        }, this.dropdownBoundHandlers);

        buttonsContainer.appendChild(selectAllBtn);
        buttonsContainer.appendChild(selectNoneBtn);
        this._dropdown.appendChild(buttonsContainer);
    }

    private createNoResultsElement(): HTMLElement {
        const noResults = document.createElement('div');
        noResults.classList.add('material-select-option', 'disabled');
        noResults.textContent = this.options.noResultsText;
        noResults.style.display = 'none'; // Initially hidden
        return noResults;
    }

    private initializeOptions() {
        if (!this.optionsList) return;
        this.allOptionElements = []; // Clear previous before repopulating
        if(Array.isArray(this.options.items)) {
            this.options.items.map(item => this.createDataItemFromValue(item)).forEach((item, index) => {
                const optionElement = this.createOptionElement(item, index);
                this.allOptionElements.push(optionElement);
                this.optionsList!.appendChild(optionElement);
            });
        }else {
            this.options.items().then(resolvedItems => {
                resolvedItems.map(item => this.createDataItemFromValue(item)).forEach((item, index) => {
                    const optionElement = this.createOptionElement(item, index);
                    this.allOptionElements.push(optionElement);
                    this.optionsList!.appendChild(optionElement);
                });
                this.filterOptions();
            });
        }
    }

    private filterOptions() {
        const query = this.options.searchable && this.searchInput ? this.searchInput.value.toLowerCase().trim() : '';
        let resultsFound = false;

        this.allOptionElements.forEach(optionElement => {
            const itemText = optionElement.textContent?.toLowerCase() || '';
            const shouldShow = itemText.includes(query);
            optionElement.style.display = shouldShow ? '' : 'none';
            if (shouldShow) {
                resultsFound = true;
            }
        });

        if (this.noResultsElement) {
            this.noResultsElement.style.display = resultsFound ? 'none' : 'block';
        }
        this.focusedOptionIndex = -1;
    }

    private createOptionElement(item: DataItem<T>, index: number): HTMLElement {
        const option = document.createElement('div');
        option.classList.add('material-select-option');
        option.id = `material-select-option-${item.value}`;
        option.setAttribute('role', 'option');
        option.setAttribute('data-value', item.value);
        option.innerHTML = this.options.itemRenderer(item);
        option.setAttribute('tabindex', '-1');

        if (this.options.multiSelect) {
            option.classList.add('material-select-multiselect');
            if (this.isItemSelected(item)) {
                option.classList.add('selected');
                option.setAttribute('aria-selected', 'true');
            }
        }
        this.addEventHandler(option, 'click', (e) => this.handleOptionClick(e, item), this.dropdownBoundHandlers);
        return option;
    }

    private handleOptionClick(e: Event, item: DataItem<T>) {
        e.stopPropagation();
        if (this.options.multiSelect) {
            this.toggleItemSelection(e.target as HTMLElement, item);
        } else {
            this.selectedItems = [item];
            this.closeDropdown();
        }
        this.updateTriggerText();
        this.options.onSelect(this.selectedItems);
        this.saveSelection();
        this.announceSelection(item);
    }

    private toggleItemSelection(element: HTMLElement, item: DataItem<T>) {
        const index = this.selectedItems.findIndex(selected => selected.value === item.value);
        if (index > -1) {
            this.selectedItems.splice(index, 1);
            element.classList.remove('selected');
            element.setAttribute('aria-selected', 'false');
        } else {
            this.selectedItems.push(item);
            element.classList.add('selected');
            element.setAttribute('aria-selected', 'true');
        }
    }

    private isItemSelected(item: DataItem<T>): boolean {
        return this.selectedItems.some(selected => selected.value === item.value);
    }

    private updateTriggerText() {
        if (this.selectedItems.length === 0) {
            this.selectTrigger.value = '';
        } else if (this.options.multiSelect) {
            this.selectTrigger.value = '';
            this.selectTrigger.placeholder = this.selectedItems.map(item => item.text).join(', ') || '';
        } else {
            this.selectTrigger.value = this.selectedItems[0].text;
        }
        if (this.inputField) {
            const label = this.inputField.querySelector('label');
            if (label) {
                if (this.selectedItems.length > 0 || this.selectTrigger.value.length > 0) {
                    label.classList.add('active');
                } else {
                    label.classList.remove('active');
                }
            }
        }
        this.syncNativeSelect();
    }

    /**
     * Mirror the current selection onto the underlying native &lt;select&gt; element.
     *
     * MaterialSelect renders its own trigger/dropdown and historically never wrote the chosen
     * value back to the (hidden) native &lt;select&gt;. Anything that read the native element
     * afterwards — {@code FormData}, {@code PanelForm.record()}, a plain {@code .value} — therefore
     * saw the stale, server-rendered value, which forced per-form workarounds (e.g. the Suppliers
     * page reading {@code .selected} off the instance directly). Syncing here fixes that for every
     * form at once.
     *
     * <p><strong>Value-only by design.</strong> We deliberately do NOT dispatch a native
     * {@code change} event here. The native select has never fired {@code change} (it was hidden
     * and never updated), so any dormant {@code change} listeners across the app are currently
     * inert — waking them all at once is a separate, riskier change.
     *
     * <p>TODO(select): hook up a user-interaction-only {@code change} dispatch in a follow-up so
     * native {@code change} listeners work like a real &lt;select&gt;. Intentionally deferred.
     */
    private syncNativeSelect() {
        const native = this._nativeSelect;
        if (!native) return;

        const selectedValues = this.selectedItems.map(item => item.value);

        // Items can be data-driven and may never have existed as real <option> nodes;
        // ensure one exists for each selected value before we try to select it.
        selectedValues.forEach(value => {
            if (!Array.from(native.options).some(option => option.value === value)) {
                const item = this.selectedItems.find(selected => selected.value === value);
                native.add(new Option(item?.text ?? value, value));
            }
        });

        if (this.options.multiSelect) {
            Array.from(native.options).forEach(option => {
                option.selected = selectedValues.includes(option.value);
            });
        } else {
            native.value = selectedValues[0] ?? '';
        }
    }

    private rediscoverElements() {
        const originalSelect = document.getElementById(this.elementId);
        if (!originalSelect) {
            console.error(`[Rediscover] FATAL: Original select element #${this.elementId} not found.`);
            this.isDestroyed = true;
            return;
        }
        const newContainer = originalSelect.closest('.material-select-container');
        if (!newContainer) {
            console.error(`[Rediscover] FATAL: Wrapper .material-select-container not found for #${this.elementId}.`);
            this.isDestroyed = true;
            return;
        }
        this.inputField = newContainer.closest('.input-field');
        const newSelectTrigger = this.inputField ? this.inputField.querySelector('.material-select-trigger') as HTMLInputElement : newContainer.querySelector('.material-select-trigger') as HTMLInputElement;

        if (!newSelectTrigger) {
            console.error(`[Rediscover] ERROR: Failed to find trigger or dropdown relative to the new container.`);
            return;
        }
        this._container = newContainer as HTMLElement;
        this.selectTrigger = newSelectTrigger;
    }

    private announceSelection(item: DataItem<T>) {
        this.liveRegion.textContent = this.isItemSelected(item) ? `Selected ${item.text}` : `Deselected ${item.text}`;
    }

    public destroy() {
        if (this.isDestroyed) return;

        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }

        // Close dropdown will clean up dropdown elements and listeners
        this.closeDropdown();

        initializedSelectInstances.delete(this.elementId);
        initializedElementInstances.delete(this.anchorElement);
        this.isDestroyed = true;

        this.removeEvents(); // Removes trigger/global listeners

        if (this.selectTrigger?.parentElement) {
            this.selectTrigger.parentElement.removeChild(this.selectTrigger);
        }
        const triggerElement = this.container.parentElement?.querySelector('.material-select-trigger');
        if(triggerElement){
            triggerElement.parentElement?.removeChild(triggerElement);
        }else{
            console.debug('No stray trigger element found during destroy.');
        }
        if (this.liveRegion?.parentElement) {
            document.body.removeChild(this.liveRegion);
        }

        this.allOptionElements = [];
        this.selectedItems = [];
    }

    public clear() {
        const wasOpen = this.isOpen();
        if(wasOpen) this.closeDropdown();

        this.options.items = [];
        this.selectedItems = [];
        this.updateTriggerText();
        this.saveSelection();
        this.allOptionElements = [];

        if(wasOpen) this.openDropdown();
    }

    public addItem(param: DataItem<T> | any) {
        if(this.options.items instanceof Function){
            this.options.items().then(data => {
                this.options.items = data;
                this.options.items.push(this.createDataItemFromValue(param));
                this.refresh();
            })
        }else if(Array.isArray(this.options)){
            this.options.items.push(param);
            this.refresh();
        }
    }

    public refresh() {
        const wasOpen = this.isOpen();
        if (wasOpen) {
            this.closeDropdown();
        }
        this.updateTriggerText();
        if (wasOpen) {
            this.openDropdown();
        }
    }

    static executeBackwardsCompatibility<T extends OptionData>(el: HTMLSelectElement) {
        const dataItems: DataItem<T>[] = Array.from(el.options).map(opt => ({
            text: opt.textContent || '',
            value: opt.value,
            data: { value: opt.value, text: opt.textContent } as T
        }));
        const selectedItems = dataItems.filter(item => (el.querySelector(`option[value="${item.value}"]`) as HTMLOptionElement)?.selected);
        return { options: dataItems, selected: selectedItems };
    }

    private static isElementConnected(element: HTMLElement): boolean {
        return element && element.isConnected && document.contains(element);
    }

    private static isInstanceValid(instance: MaterialSelect<any>): boolean {
        return instance &&
            !instance.isDestroyed &&
            instance._container &&
            MaterialSelect.isElementConnected(instance._container);
    }

    public static init<T extends OptionData>(
        element: string | HTMLElement | NodeList | HTMLElement[] | MaterialSelect<T>,
        options: MaterialSelectOptions<T> = {}
    ): MaterialSelect<T> {
        if (!element) throw new Error('MaterialSelect: No element provided to initialize.');
        if (element instanceof MaterialSelect) {
            if (MaterialSelect.isInstanceValid(element)) {
                element.updateOptions(options);
                element.refresh();
                element.recalculateDimensions();
                return element;
            } else {
                element.destroy();
                throw new Error('MaterialSelect instance is no longer valid. Please initialize with a new element.');
            }
        }

        let elementsToProcess: HTMLElement[] = [];
        if (typeof element === 'string') {
            elementsToProcess = Array.from(document.querySelectorAll(element));
        } else if (element instanceof HTMLElement) {
            elementsToProcess = [element];
        } else {
            elementsToProcess = Array.from(element) as HTMLElement[];
        }

        if (elementsToProcess.length === 0) {
            throw new Error('MaterialSelect: No valid elements found to initialize.');
        }

        const instances: MaterialSelect<T>[] = [];

        elementsToProcess.forEach(el => {
            // --- Smart Instance Check ---
            let salvagedSelection: DataItem<T>[] | undefined = undefined;

            // 1. Primary check: Use the element itself as the key.
            let existingInstance = initializedElementInstances.get(el);
            if (existingInstance) {
                if (MaterialSelect.isInstanceValid(existingInstance)) {
                    console.debug(`Reusing valid MaterialSelect instance found via element reference for:`, el);
                    existingInstance.updateOptions(options);
                    existingInstance.refresh();
                    existingInstance.recalculateDimensions();
                    instances.push(existingInstance);
                    return; // Done with this element, continue to next.
                } else {
                    // If we found an invalid instance, destroy it before proceeding.
                    console.debug(`Destroying invalid MaterialSelect instance found via element reference for:`, el);
                    salvagedSelection = existingInstance.selectedItems;
                    existingInstance.destroy();
                }
            }

            // 2. Fallback check: Use the element's ID. This is useful if the DOM was re-rendered.
            if (el.id) {
                const instanceById = initializedSelectInstances.get(el.id);
                if (instanceById && !MaterialSelect.isInstanceValid(instanceById)) {
                    console.debug(`Found zombie instance for ID ${el.id}. Salvaging state.`);
                    salvagedSelection = instanceById.selectedItems;
                    instanceById.destroy();
                }
            }

            let finalOptions = { ...options };

            // Use salvaged state if available and no new initialSelection is provided.
            if (salvagedSelection && salvagedSelection.length > 0 && !options.initialSelection) {
                finalOptions.initialSelection = salvagedSelection;
            }

            if (el.tagName.toLowerCase() === 'select') {
                const compatData = MaterialSelect.executeBackwardsCompatibility(el as HTMLSelectElement);
                finalOptions.items = (finalOptions.items || compatData.options) as DataItem<T>[];
                finalOptions.multiSelect = el.hasAttribute('multiple');
                if (compatData.selected.length > 0 && !finalOptions.initialSelection) {
                    finalOptions.initialSelection = compatData.selected as DataItem<T>[];
                }
            }

            const newInstance = new MaterialSelect<T>(el, finalOptions);
            // Store in both maps for robust lookup.
            initializedSelectInstances.set(newInstance.elementId, newInstance);
            initializedElementInstances.set(el, newInstance);
            instances.push(newInstance);
        });

        if (instances.length === 0) {
            throw new Error('MaterialSelect: Failed to initialize any valid instances.');
        }

        return instances[0];
    }

    private static isElementValid<T extends OptionData>(instance: MaterialSelect<T>, currentElement: HTMLElement): boolean {
        // Now also checks if the trigger element is part of the DOM tree
        return instance &&
            !instance.isDestroyed &&
            instance._container &&
            MaterialSelect.isElementConnected(instance._container) &&
            instance.selectTrigger &&
            MaterialSelect.isElementConnected(instance.selectTrigger);
    }

    removeItem(itemToRemove: DataItem<OptionData>) {
        if(this.options.items instanceof Function){
            this.options.items().then(data => {
                //Remove item from new data
                this.options.items = data.filter(item => {
                    const dataItem = this.isDataItem(item) ? item : this.createDataItemFromValue(item);
                    return dataItem.value !== itemToRemove.value;
                });
                //Also remove from selected items if present
                const selectedIndex = this.selectedItems.findIndex(item => item.value === itemToRemove.value);
                if (selectedIndex > -1) {
                    this.selectedItems.splice(selectedIndex, 1);
                    this.options.onSelect(this.selectedItems);
                    this.saveSelection();
                }
                this.refresh();
            })
        }else {
            const index = this.options.items.findIndex(item => {
                const dataItem = this.isDataItem(item) ? item : this.createDataItemFromValue(item);
                return dataItem.value === itemToRemove.value;
            });
            if (index > -1) {
                this.options.items.splice(index, 1);
                const selectedIndex = this.selectedItems.findIndex(item => item.value === itemToRemove.value);
                if (selectedIndex > -1) {
                    this.selectedItems.splice(selectedIndex, 1);
                    this.options.onSelect(this.selectedItems);
                    this.saveSelection();
                }
                this.refresh();
            }
        }
    }

    public static cleanupOrphanedInstances() {
        const toRemove: string[] = [];
        initializedSelectInstances.forEach((instance, elementId) => {
            if (!MaterialSelect.isInstanceValid(instance)) {
                console.debug(`Cleaning up orphaned MaterialSelect instance: ${elementId}`);
                instance.destroy();
                toRemove.push(elementId);
            }
        });
        toRemove.forEach(id => initializedSelectInstances.delete(id));
    }

    public static getActiveInstances(): MaterialSelect<any>[] {
        MaterialSelect.cleanupOrphanedInstances();
        return Array.from(initializedSelectInstances.values());
    }

    public static getInstance<T extends OptionData>(elementId: string): MaterialSelect<T> | null {
        const instance = initializedSelectInstances.get(elementId);
        if (instance && MaterialSelect.isInstanceValid(instance)) {
            return instance as MaterialSelect<T>;
        }
        return null;
    }

    setSelectListener(param: (item: DataItem<T> | DataItem<T>[] ) => void) {
        this.options.onSelect = param;
    }
}
